// Upload progress for a file sent through a Relay mutation.
//
// fetch reports no upload progress, so a mutation whose uploadables include a file someone is
// tracking is sent with XMLHttpRequest instead, by uploadProgressMiddleware below, and its progress
// goes to that file's listener. Every other request still goes through fetch, unchanged. The request
// itself is identical either way: same URL, headers, credentials and multipart body, and the response
// is handed back as a fetch Response, so the Relay network layer above (retries, error handling)
// cannot tell the two apart.

export type UploadProgressListener = (fraction: number) => void;

const listeners = new WeakMap<Blob, UploadProgressListener>();
// The request currently sending each tracked file. The retry middleware starts a new attempt when one
// takes too long without cancelling the old one, so a new attempt aborts the request it replaces:
// otherwise both would keep uploading and report progress into the same row.
const inFlight = new WeakMap<UploadProgressListener, XMLHttpRequest>();

// Statuses whose response has no body; a Response given one, even an empty string, throws.
const NULL_BODY_STATUSES = new Set([101, 204, 205, 304]);

// Reports the upload progress of `file` to `listener`, as a fraction from 0 to 1, whenever a request
// carries it. Returns the call that stops tracking it. Pass the File the mutation uploads: FormData keeps
// a File as the same object, but wraps any other Blob in a new one, which this could not recognise.
export function trackUploadProgress(file: Blob, listener: UploadProgressListener): () => void {
	listeners.set(file, listener);
	return () => {
		if (listeners.get(file) === listener) {
			listeners.delete(file);
		}
	};
}

function trackedListener(body: unknown): UploadProgressListener | null {
	if (typeof FormData === 'undefined' || !(body instanceof FormData)) {
		return null;
	}
	let found: UploadProgressListener | null = null;
	body.forEach(value => {
		if (!found && value instanceof Blob) {
			found = listeners.get(value) ?? null;
		}
	});
	return found;
}

function responseHeaders(xhr: XMLHttpRequest): Headers {
	const headers = new Headers();
	xhr.getAllResponseHeaders()
		.trim()
		.split(/[\r\n]+/)
		.forEach(line => {
			const separator = line.indexOf(':');
			if (separator > 0) {
				headers.append(line.slice(0, separator).trim(), line.slice(separator + 1).trim());
			}
		});
	return headers;
}

interface UploadRequest {
	fetchOpts: {
		url?: string;
		method?: string;
		headers: Record<string, string>;
		body?: unknown;
		credentials?: string;
	};
}

export function sendWithProgress(req: UploadRequest, listener: UploadProgressListener): Promise<Response> {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		inFlight.get(listener)?.abort();
		inFlight.set(listener, xhr);
		const settle = (): void => {
			if (inFlight.get(listener) === xhr) {
				inFlight.delete(listener);
			}
		};
		xhr.open(req.fetchOpts.method ?? 'POST', req.fetchOpts.url ?? '/graphql');
		xhr.withCredentials = req.fetchOpts.credentials === 'include';
		Object.entries(req.fetchOpts.headers ?? {}).forEach(([name, value]) => {
			// The browser writes a multipart body's Content-Type itself, boundary included.
			if (name.toLowerCase() !== 'content-type') {
				xhr.setRequestHeader(name, value);
			}
		});
		xhr.upload.onprogress = event => {
			if (event.lengthComputable && event.total > 0) {
				listener(Math.min(1, event.loaded / event.total));
			}
		};
		xhr.onload = () => {
			settle();
			listener(1);
			const body = NULL_BODY_STATUSES.has(xhr.status) ? null : xhr.responseText;
			resolve(new Response(body, {status: xhr.status, statusText: xhr.statusText, headers: responseHeaders(xhr)}));
		};
		// The same error fetch raises for a request that never got a response, so the retry and
		// error handling above treat it exactly as they would a failed fetch.
		const failed = () => {
			settle();
			reject(new TypeError('Failed to fetch'));
		};
		xhr.onerror = failed;
		xhr.ontimeout = failed;
		xhr.onabort = failed;
		xhr.send(req.fetchOpts.body as XMLHttpRequestBodyInit);
	});
}

type RawNext = (req: UploadRequest) => Promise<Response>;

// A raw-fetch middleware for react-relay-network-modern: it sits where fetch is called and returns a
// fetch Response, so it must be registered with isRawMiddleware.
export const uploadProgressMiddleware = Object.assign(
	(next: RawNext) =>
		(req: UploadRequest): Promise<Response> => {
			const listener = trackedListener(req.fetchOpts.body);
			return listener ? sendWithProgress(req, listener) : next(req);
		},
	{isRawMiddleware: true}
);
